import React, { createContext, useContext, useRef } from "react";
import { createPcpLiveLessonStore, PcpLiveLessonStore } from "./pcpLiveLessonStore";
import { StatefulCallClient, StatefulChatClient } from "@azure/communication-react";
import { useStore } from "zustand";
import { useSyncClientStates } from "./hooks/useSyncClientStates";
import { PcpLiveLessonState } from "./pcpLiveLessonStore.interface";
import { CreatePcpSharedContextProps, useSyncSharedSlice } from "./slices/pcpSharedSlice";


interface PcpLiveLessonStoreContextProps {
    callClient: StatefulCallClient;
    chatClient: StatefulChatClient;
    sharedProps: CreatePcpSharedContextProps;

    children: React.ReactNode;
}

export const PcpLiveLessonStoreContext = createContext<PcpLiveLessonStore | undefined>(undefined);


export const PcpLiveLessonStoreContextProvider = (props: PcpLiveLessonStoreContextProps): React.ReactNode => {
    const { children, callClient, chatClient, sharedProps } = props;

    const storeRef = useRef<PcpLiveLessonStore>();
    if (!storeRef.current) {
        storeRef.current = createPcpLiveLessonStore(callClient, chatClient, sharedProps);
    }

    useSyncClientStates(storeRef.current, callClient, chatClient);
    useSyncSharedSlice(storeRef.current);

    return (
        <PcpLiveLessonStoreContext.Provider value={storeRef.current}>
            {children}
        </PcpLiveLessonStoreContext.Provider>
    );
};

export const usePcpLiveLessonStore = (): PcpLiveLessonStore => {
    const store = React.useContext(PcpLiveLessonStoreContext);
    if (!store) {
        throw new Error("usePcpLiveLessonStore must be used within a PcpLiveLessonStoreContextProvider");
    }
    return store;
};

export function usePcpLiveLessonContext<T>(selector: (state: PcpLiveLessonState) => T): T {
    const store = useContext(PcpLiveLessonStoreContext)
    if (!store) throw new Error('Missing PcpLiveLessonStoreContext.Provider in the tree')

    return useStore(store, selector)
}
